import React from 'react'
import DOMPurify from 'dompurify'

interface SafeHtmlRendererProps {
  htmlContent: string
  className?: string
  style?: React.CSSProperties
  tag?: keyof JSX.IntrinsicElements
}

const SafeHtmlRenderer: React.FC<SafeHtmlRendererProps> = ({
  htmlContent,
  className = '',
  style = {},
  tag: Tag = 'div'
}) => {
  // Check if content is actually HTML or just plain text
  const isHtml = (content: string): boolean => {
    if (!content) return false
    return content.includes('<') && content.includes('>')
  }

  // If it's not HTML, just return the plain text
  if (!htmlContent || !isHtml(htmlContent)) {
    return (
      <Tag className={className} style={style}>
        {htmlContent}
      </Tag>
    )
  }

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'code', 'pre',
      'a', 'img',
      'span', 'div'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 
      'src', 'alt', 'width', 'height',
      'style', 'class',
      'data-*'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+\.\-]+(?:[^a-z+\.\-:]|$))/i
  })

  // Post-process anchors to ensure safe defaults and interactivity
  const enhancedHtml = sanitizedHtml.replace(/<a(\s+[^>]*)?>/gi, (match) => {
    let tag = match
    if (!/target=/i.test(tag)) tag = tag.replace('<a', '<a target="_blank"')
    if (!/rel=/i.test(tag)) tag = tag.replace('<a', '<a rel="noopener noreferrer"')
    return tag
  })

  return (
    <Tag 
      className={`rich-text-content ${className}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: enhancedHtml }}
    />
  )
}

export default SafeHtmlRenderer